/*
Organization Locations Service

This service handles all API calls for organization locations.
Following established patterns from PROJECT_CONVENTIONS.md:
- Uses axios with withCredentials for session authentication
- Standardized response format conversion
- Error handling with proper status codes
- Base URL configuration

TODO: Phase 2 Implementation Tasks:
1. Implement all CRUD operations with proper error handling
2. Add request/response type validation
3. Integrate with authentication system and handle 401s
4. Add retry logic for failed requests
5. Implement caching for frequently accessed data
6. Add address validation and geocoding services
7. Add map integration APIs
8. Implement location search by coordinates
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

// Use shared apiClient instead of custom instance
const apiService = apiClient;

// Types (TODO: Import from shared types)
export interface OrganizationLocation {
  id: number;
  entity_id: number;
  name: string;
  parent_location_id?: number;
  installation_date?: string;
  is_physical: boolean;
  address?: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  registration_id_1?: string;
  registration_id_2?: string;
  registration_id_3?: string;
  is_active: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

export interface LocationQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  type?: string;
  country?: string;
  search?: string;
  tree?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface LocationStatistics {
  total_locations: number;
  active_locations: number;
  inactive_locations: number;
  locations_by_type: Record<string, number>;
  locations_by_country: Record<string, number>;
}

export interface LocationType {
  value: string;
  label: string;
  description?: string;
}

class OrganizationLocationsService {

  /**
   * Get all organization locations with optional filtering
   * TODO: Implement with proper error handling and response transformation
   */
  async getAll(params?: LocationQueryParams): Promise<OrganizationLocation[]> {
    try {
      // TODO: Build query string from parameters
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') {
        queryParams.append('status', params.status);
      }
      if (params?.type && params.type !== 'ALL') {
        queryParams.append('type', params.type);
      }
      if (params?.country && params.country !== 'ALL') {
        queryParams.append('country', params.country);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.tree) {
        queryParams.append('tree', 'true');
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/org/locations${queryString ? `?${queryString}` : ''}`;

      // TODO: Make actual API call
      const response: AxiosResponse<ApiResponse<OrganizationLocation[]>> =
        await apiService.get(url);

      // Backend returns { data: [], meta: {} } structure directly
      // We need to adapt it to the service's expected return type
      // The backend response is Dict[str, Any] -> { "data": [...], "meta": ... }
      // The service expects OrganizationLocation[]
      if (response.data && (response.data as any).data) {
        return (response.data as any).data;
      }
      return [];

    } catch (error: any) {
      console.error('Error fetching locations:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch organization locations');
    }
  }

  /**
   * Approved + active locations only (entity-scoped, unbounded, sorted by name).
   * Use for form dropdowns/pickers instead of getAll() which returns all
   * workflow states (draft/submitted/rejected leaked into pickers otherwise).
   */
  async getDropdown(): Promise<{ id: number; name: string }[]> {
    try {
      const response: AxiosResponse<{ total: number; eligible: number; items: { id: number; name: string; code?: string }[] }> =
        await apiService.get('/api/v1/org/locations/dropdown');
      return response.data?.items ?? [];
    } catch (error: any) {
      console.error('Error fetching locations dropdown:', error);
      return [];
    }
  }

  /**
   * Get a specific location by ID
   * TODO: Implement location retrieval by ID
   */
  async getById(locationId: string): Promise<OrganizationLocation> {
    try {
      // TODO: Make API call to fetch specific location
      const response: AxiosResponse<ApiResponse<OrganizationLocation>> =
        await apiService.get(`/api/v1/org/locations/${locationId}`);

      if (response.data) {
        return response.data as any;
      } else {
        throw new Error('Location not found');
      }

    } catch (error: any) {
      console.error(`Error fetching location ${locationId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch location');
    }
  }

  /**
   * Create a new organization location
   * TODO: Implement location creation with address validation
   */
  async create(locationData: Partial<OrganizationLocation>): Promise<OrganizationLocation> {
    try {
      // TODO: Validate required fields before API call
      if (!locationData.name) {
        throw new Error('Location name is required');
      }

      // TODO: Add address validation
      const validationResult = await this.validateAddress(locationData);
      if (!validationResult.valid) {
        throw new Error(`Address validation failed: ${validationResult.errors.join(', ')}`);
      }

      // TODO: Make API call to create location
      const response: AxiosResponse<ApiResponse<OrganizationLocation>> =
        await apiService.post('/api/v1/org/locations', locationData);

      if (response.data) {
        return response.data as any;
      } else {
        throw new Error('Failed to create location');
      }

    } catch (error: any) {
      console.error('Error creating location:', error);
      throw new Error(error.response?.data?.error || 'Failed to create location');
    }
  }

  /**
   * Update an existing organization location
   * TODO: Implement location update with validation
   */
  async update(locationId: string, updateData: Partial<OrganizationLocation>): Promise<OrganizationLocation> {
    try {
      // TODO: Make API call to update location
      const response: AxiosResponse<ApiResponse<OrganizationLocation>> =
        await apiService.put(`/api/v1/org/locations/${locationId}`, updateData);

      if (response.data) {
        return response.data as any;
      } else {
        throw new Error('Failed to update location');
      }

    } catch (error: any) {
      console.error(`Error updating location ${locationId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to update location');
    }
  }

  /**
   * Delete an organization location
   * TODO: Implement location deletion with confirmation
   */
  async delete(locationId: string, hardDelete: boolean = false): Promise<void> {
    try {
      // TODO: Add confirmation dialog in calling component
      const queryParam = hardDelete ? '?hard=true' : '';

      // TODO: Make API call to delete location
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`/api/v1/org/locations/${locationId}${queryParam}`);

      if (response.status !== 204) {
        // 204 No Content is expected
        // But if it returns something else, check for error
      }

    } catch (error: any) {
      console.error(`Error deleting location ${locationId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to delete location');
    }
  }

  /**
   * Get locations in hierarchical tree structure
   * TODO: Implement tree structure retrieval
   */
  async getTree(): Promise<OrganizationLocation[]> {
    try {
      // For now, we'll fetch all and build the tree if needed, 
      // or just return all and let the component handle it if it's simple.
      // The backend doesn't have a specific tree endpoint yet, so we use getAll with tree=true
      return await this.getAll({ tree: true });
    } catch (error: any) {
      console.error('Error fetching location tree:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch location tree');
    }
  }

  /**
   * Get locations filtered by type
   * TODO: Implement location type filtering
   */
  async getByType(locationType: string): Promise<OrganizationLocation[]> {
    try {
      return await this.getAll({ type: locationType });
    } catch (error: any) {
      console.error(`Error fetching locations by type ${locationType}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch locations by type');
    }
  }

  /**
   * Get list of valid location types
   * TODO: Implement location types retrieval
   */
  async getLocationTypes(): Promise<string[]> {
    try {
      const response: AxiosResponse<string[]> =
        await apiService.get('/api/v1/org/locations/types');

      if (response.data) {
        return response.data;
      } else {
        throw new Error('Failed to fetch location types');
      }

    } catch (error: any) {
      console.error('Error fetching location types:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch location types');
    }
  }

  /**
   * Get location statistics
   * TODO: Implement statistics retrieval
   */
  async getStatistics(): Promise<LocationStatistics> {
    try {
      const response: AxiosResponse<LocationStatistics> =
        await apiService.get('/api/v1/org/locations/stats');

      if (response.data) {
        return response.data;
      } else {
        throw new Error('Failed to fetch statistics');
      }

    } catch (error: any) {
      console.error('Error fetching location statistics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch location statistics');
    }
  }

  /**
   * Validate location data without creating/updating
   * TODO: Implement client-side and server-side validation
   */
  async validateData(locationData: Partial<OrganizationLocation>, isUpdate: boolean = false): Promise<ValidationResult> {
    try {
      // TODO: Add client-side validation first
      const clientErrors = this.validateClientSide(locationData, isUpdate);
      if (clientErrors.length > 0) {
        return { valid: false, errors: clientErrors };
      }

      // TODO: Make API call for server-side validation
      const queryParam = isUpdate ? '?update=true' : '';
      const response: AxiosResponse<ValidationResult> =
        await apiService.post(`/organization/locations/validate${queryParam}`, locationData);

      return response.data;

    } catch (error: any) {
      console.error('Error validating location data:', error);
      return {
        valid: false,
        errors: ['Validation service error']
      };
    }
  }

  /**
   * Validate address information
   * TODO: Implement address validation with external services
   */
  async validateAddress(locationData: Partial<OrganizationLocation>): Promise<ValidationResult> {
    try {
      // TODO: Client-side address validation
      const errors: string[] = [];

      // TODO: Validate address format
      if (locationData.contact_email && !this.isValidEmail(locationData.contact_email)) {
        errors.push('Invalid email address format');
      }

      if (locationData.contact_phone && !this.isValidPhoneNumber(locationData.contact_phone)) {
        errors.push('Invalid phone number format');
      }

      if (errors.length > 0) {
        return { valid: false, errors };
      }

      // TODO: Use external address validation service (Google, etc.)
      return { valid: true, errors: [] };

    } catch (error: any) {
      console.error('Error validating address:', error);
      return {
        valid: false,
        errors: ['Address validation service error']
      };
    }
  }

  /**
   * Format address as single string
   * TODO: Implement address formatting with internationalization
   */
  formatAddress(location: OrganizationLocation): string {
    try {
      // TODO: Format address based on country standards
      const parts = [
        location.address,
        location.contact_person,
        location.contact_phone,
        location.contact_email
      ].filter(Boolean);

      return parts.length > 0 ? parts.join(', ') : 'No address provided';

    } catch (error: any) {
      console.error('Error formatting address:', error);
      return 'Address formatting error';
    }
  }

  /**
   * Client-side validation helper
   * TODO: Implement comprehensive validation rules
   */
  private validateClientSide(data: Partial<OrganizationLocation>, isUpdate: boolean): string[] {
    const errors: string[] = [];

    // TODO: Add validation rules
    if (!isUpdate) {
      if (!data.name) {
        errors.push('Location name is required');
      }
    }

    // TODO: Add format validation
    // if (data.location_code && !/^[A-Z0-9_]{2,10}$/.test(data.location_code)) {
    //   errors.push('Location code must be 2-10 uppercase letters, numbers, or underscores');
    // }

    return errors;
  }

  /**
   * Email validation helper
   * TODO: Implement robust email validation
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Phone number validation helper
   * TODO: Implement international phone number validation
   */
  private isValidPhoneNumber(phone: string): boolean {
    // TODO: Use libphonenumber or similar for proper validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Geocode address to coordinates
   * TODO: Implement geocoding service integration
   */
  async geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    try {
      // TODO: Integrate with Google Maps Geocoding API or similar
      console.log('Geocoding not yet implemented for address:', address);
      return null;

    } catch (error: any) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Check service health
   * TODO: Implement health check for monitoring
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      // TODO: Make API call to health endpoint
      const response: AxiosResponse<{ module: string; status: string; timestamp: string }> =
        await apiService.get('/organization/locations/health');

      return {
        status: response.data.status,
        timestamp: response.data.timestamp
      };

    } catch (error: any) {
      console.error('Location service health check failed:', error);
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Cancel ongoing requests (for component cleanup)
   * TODO: Implement request cancellation
   */
  cancelRequests(): void {
    // TODO: Implement AbortController for request cancellation
    console.log('Request cancellation not yet implemented');
  }
}

// Export singleton instance
const organizationLocationsService = new OrganizationLocationsService();
export default organizationLocationsService;